import { useCallback, useMemo } from 'react'
import { getContrastRatio } from '@material-ui/core/styles'
import { getSectionTheme, updatePaletteTextTheme } from '@v4/utils/theme'
import ScorecardSelector from '@v4/utils/selectors/ScorecardSelector'
import AppStore from '@v4/store/appStore'
import PageBuilderSelector from '@v4/utils/selectors/PageBuilderSelector'
import useQuestionId from '@v4/hooks/questions/useQuestionId'
import useQuestionSettingsField from '@v4/hooks/questions/useQuestionSettingsField'
import useQuestionField from '@v4/hooks/questions/useQuestionField'
import QUESTION_TYPE from '@v4/constants/quiz'
import useWordings from '@v4/hooks/useWordings'
import QuestionsBuilderSelector from '@v4/utils/selectors/QuestionsBuilderSelector'
import { useMediaQuery, useTheme } from '@material-ui/core'
import PageMetaData from '@v4/utils/data/PageMetaData'
import ThemeSelector from '@v4/utils/selectors/ThemeSelector'
import {
	getMediaSortDirection,
	isPositionBackground,
	isPositionContained,
	isPositionCover,
	isSingleGrid,
} from './Question.utils'
import { MEDIA_POSITION, SRC_SET } from './Question.const'
import useStyles from './Question.styled'
import { useSectionMetaData } from '../../common/SectionItem'

export const useQuestion = () => {
	const metaData = useSectionMetaData()
	const {
		backgroundImage,
		primary,
		textPrimary,
		instructionColor,
		textSecondary,
		textShadow,
		align,
		textSize,
		useOldCheckboxes,
		instructionPosition,
		backButtonText,
	} = metaData

	const settingsFallback = ScorecardSelector.useSettings()
	const withMetaBgImage = !!backgroundImage?.image
	const persist = PageBuilderSelector.useIsDataEqual()
	const visibleInstruction = useQuestionSettingsField('show_instruction')
	const isBuilderMode = AppStore.useStoreState(state => state.general.isBuilderMode)
	const locked = AppStore.useStoreState(state => state.question.locked)
	const questionId = useQuestionId()
	const answerType = useQuestionField(questionId, 'answer_type')
	const subType = useQuestionSettingsField('type')
	const { getWording } = useWordings()
	const asset = useQuestionField(questionId, 'asset')
	const desktopPosition = useQuestionSettingsField('desktop_position')
	const mobilePosition = useQuestionSettingsField('mobile_position')
	const shading = useQuestionSettingsField('shading')
	const objectFit = useQuestionSettingsField('image_fit')
	const autoplay = useQuestionSettingsField('autoplay')
	const invertColors = useQuestionSettingsField('invert_colors')
	const theme = useTheme()
	const textDirection = theme.direction || 'ltr'
	const isMobile = useMediaQuery(theme.breakpoints.down('xs'))
	const minMarginTop = AppStore.useStoreState(state => state.ui.sectionHeights.header)
	const gallery = QuestionsBuilderSelector.useGallery()
	const position = isMobile ? mobilePosition : desktopPosition
	const isQuestionGallery = gallery?.entityId === questionId && gallery?.entityType === 'question'
	const galleryPreview = isQuestionGallery ? gallery.preview : null
	const sectionKey = 'theme/header-1'

	const headerMarginTop = ThemeSelector.useSectionMeta('marginTop', sectionKey)
	const headerPosition = ThemeSelector.useSectionMeta('position', sectionKey)

	const previewAssetType = isQuestionGallery ? gallery.preview?.type : null
	const assetType = previewAssetType || asset?.type

	const previewPath = isQuestionGallery ? gallery.preview?.path : null
	const showMedia = !!previewPath || !!asset?.path

	const isAssetImage = assetType === PageMetaData.ASSET_TYPE.IMAGE

	const withBgAsset = MEDIA_POSITION.BACKGROUND === position && showMedia
	const withBackgroundImage = withMetaBgImage || withBgAsset
	const alignFlex = align?.metaValue

	const classes = useStyles({
		...metaData,
		shading,
		position,
		isMobile,
		showMedia,
		isAssetImage,
		headerMarginTop,
		headerPosition,
		minMarginTop,
		alignFlex,
	})

	const type = answerType && subType ? [answerType, subType].join('_') : null

	const withOldCheckboxes = (!isBuilderMode || persist) && !!useOldCheckboxes?.toBoolean()

	const withoutContrast = !withOldCheckboxes || (withOldCheckboxes && withBackgroundImage)

	const brandColourPrimary = settingsFallback.brand_colour_primary
	const primaryColor = primary?.metaValue
	const textPrimaryColor = textPrimary?.metaValue
	const textSecondaryColor = textSecondary?.metaValue
	const instructionSecondaryColor = instructionColor?.metaValue

	const assetMetaData = useMemo(() => {
		return new PageMetaData(
			null,
			{
				type: PageMetaData.FIELD_TYPE.ASSET,
				srcSet: SRC_SET,
			},
			{},
			{
				preview: galleryPreview,
			},
		).fill({
			asset,
			meta_value: '',
		})
	}, [asset, galleryPreview])

	const sectionTheme = useCallback(
		parentTheme => {
			const isDefault = primaryColor === brandColourPrimary

			if (!isDefault || getContrastRatio(brandColourPrimary, parentTheme.palette.background.default) > 1.016) {
				return parentTheme
			}

			return getSectionTheme(parentTheme, {
				palette: {
					primary: parentTheme.palette.calcContrastColor(brandColourPrimary),
				},
			})
		},
		[primaryColor, brandColourPrimary],
	)

	const backwardsActionTheme = useCallback(
		parentTheme =>
			withoutContrast
				? updatePaletteTextTheme(parentTheme, {
						secondary: textPrimaryColor,
				  })
				: getSectionTheme(parentTheme, {
						palette: { textSecondary: parentTheme.palette.text.primary },
				  }),
		[textPrimaryColor, withoutContrast],
	)

	const categoryInfoTheme = useCallback(
		parentTheme =>
			withoutContrast
				? updatePaletteTextTheme(parentTheme, {
						primary: textPrimaryColor,
				  })
				: parentTheme,
		[textPrimaryColor, withoutContrast],
	)

	const instructionsTheme = useCallback(
		parentTheme =>
			withoutContrast
				? updatePaletteTextTheme(parentTheme, {
						secondary: instructionSecondaryColor,
				  })
				: getSectionTheme(parentTheme, {
						palette: {
							textSecondary: parentTheme.palette.common.instructionColor,
						},
				  }),
		[instructionSecondaryColor, withoutContrast],
	)

	const questionTheme = useCallback(
		parentTheme => {
			// if (withBgAsset) return getWhiteTheme(parentTheme)

			return withoutContrast
				? updatePaletteTextTheme(parentTheme, {
						primary: textPrimaryColor,
						secondary: textSecondaryColor,
				  })
				: parentTheme
		},
		[textPrimaryColor, textSecondaryColor, withoutContrast],
	)

	const handleLockClick = useCallback(event => {
		const { action } = event.target.dataset
		if (!action) {
			return
		}
		event.preventDefault()
		switch (action) {
			case 'go_back':
				if (document.referrer) {
					window.location = document.referrer
				} else {
					window.location.reload()
				}
				break
			case 'view_result':
				window.history.forward()
				break
			default:
				break
		}
	}, [])

	const direction = getMediaSortDirection(position, textDirection)

	const isSingleGridItem = !showMedia || isSingleGrid(position)
	const isCoverPosition = isPositionCover(position)
	const isBackground = isPositionBackground(position)
	const isContainedPosition = isPositionContained(position)

	return [
		{
			classes,
			withShadow: withBackgroundImage && textShadow?.toBoolean(),
			align: alignFlex,
			backButtonLabel: backButtonText?.metaValue ?? getWording('back'),
			sectionTheme,
			backwardsActionTheme,
			categoryInfoTheme,
			instructionsTheme,
			questionTheme,
			useOldCheckboxes: withOldCheckboxes,
			metaData,
			showCategory: metaData.showCategory?.toBoolean(),
			withBgImage: withBackgroundImage,
			textSize: textSize?.metaValue,
			locked,
			lockedQuestionTitle: getWording('lockedQuestionTitle'),
			lockedQuestionText: getWording('lockedQuestionText'),
			questionId,
			instructionPosition: instructionPosition?.metaValue,
			visibleInstruction: type !== QUESTION_TYPE.PLACEHOLDER ? visibleInstruction : false,
			visible: !isBuilderMode || !!questionId,
			type,

			invertColors: invertColors && withBgAsset,
			gridKey: showMedia ? 'asset' : 'none',
			showMedia,
			shading,
			position,
			key: isMobile ? 'mobile' : 'desktop',
			direction,
			autoplay,
			singleGridItemProps: isSingleGridItem ? { sm: 8, xs: 12 } : { lg: 6, md: 6, sm: 12, xs: 12 },
			isSingleGridItem,
			showTopCover: position === MEDIA_POSITION.TOP_COVER && showMedia,
			isContainedPosition: isContainedPosition && showMedia,
			isCoverPosition: isCoverPosition && showMedia,
			showMetaBgImage: withMetaBgImage && !(showMedia && isBackground),
			metaBackgroundImage: backgroundImage,
			isBackground: isBackground && showMedia,
			aboveOrBelow:
				_.includes([MEDIA_POSITION.BELOW_QUESTION, MEDIA_POSITION.ABOVE_QUESTION], position) && showMedia,
			aboveAndBelowObjectFit: objectFit || 'contain',
			assetMetaData,
			withOldTextSize: metaData.withOldTextSize?.toBoolean() || false,
		},
		{
			handleLockClick,
		},
	]
}
